import { TableV3ColDefs } from 'common/table';
import { ICellRendererParams } from 'ag-grid-community';
import { RouterLink } from 'common/ui/RouterLink';
import { CandidateProfileTalentListTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTalentListTable';
import { SearchableCandidateProfileListSortableFields } from 'generated-graphql/types';
import { formatDateFromNowAbbreviated } from 'utils/datetime';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { pathsV2Creators } from 'core/services/navigation/usePathsV2';
import { TableV3FilterItem } from 'common/table/types/TableV3';

type GetCandidateProfileTalentListTableColumnsProps = {
  candidateProfileListProps: CandidateProfileListProps;
  actionsRenderer: (
    data: CandidateProfileTalentListTableColumns
  ) => React.ReactNode;
  // Loads the Team filter's options; no Team column when teams are off.
  onTeamSearch?: (query: string) => Promise<TableV3FilterItem[]>;
};

export const getCandidateProfileTalentListTableColumns = ({
  candidateProfileListProps,
  actionsRenderer,
  onTeamSearch
}: GetCandidateProfileTalentListTableColumnsProps): TableV3ColDefs<
  CandidateProfileTalentListTableColumns,
  SearchableCandidateProfileListSortableFields
> => [
  {
    field: 'title',
    headerName: 'List name',
    flex: 1,
    minWidth: 200,
    searchable: true,
    cellRenderer: (
      params: ICellRendererParams<CandidateProfileTalentListTableColumns>
    ) => {
      if (!params.data) return null;
      switch (candidateProfileListProps.type) {
        case CandidateProfileListType.CANDIDATES_LIST:
          return (
            <RouterLink
              to={pathsV2Creators.TALENT_POOL_DETAILS({
                talentPoolId: params.data.id
              })}
            >
              {params.data.title}
            </RouterLink>
          );
        case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
          return (
            <RouterLink
              to={pathsV2Creators.ANALYSE_TALENT_POOL_DETAILS({
                candidateBatchId: candidateProfileListProps.candidateBatchId,
                talentPoolId: params.data.id
              })}
            >
              {params.data.title}
            </RouterLink>
          );
        case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
        case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
        case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
          // Not applicable — these views are accessed from within a talent pool, so this column is hidden
          return null;
      }
    }
  },
  {
    field: 'candidatesCount',
    headerName: 'Candidates',
    width: 150,
    sortField:
      SearchableCandidateProfileListSortableFields.CandidateProfilesCount
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 200,
    searchable: true
  },
  {
    field: 'updatedAt',
    headerName: 'Updated',
    width: 180,
    sortField: SearchableCandidateProfileListSortableFields.UpdatedAt,
    cellRenderer: (
      params: ICellRendererParams<CandidateProfileTalentListTableColumns>
    ) => {
      if (!params.value) return null;
      return formatDateFromNowAbbreviated(params.value);
    }
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 180
  },
  ...(onTeamSearch
    ? [
        {
          field: 'teams' as const,
          headerName: 'Team',
          width: 200,
          serverFilterOptions: {
            searchPlaceholder: 'Search teams',
            selectionType: 'multiple' as const,
            minSearchLength: 1,
            showCheckboxes: true,
            requiresApply: true,
            onSearchFilter: onTeamSearch
          },
          cellRenderer: (
            params: ICellRendererParams<CandidateProfileTalentListTableColumns>
          ) => params.data?.teams.map(({ name }) => name).join(', ')
        }
      ]
    : []),
  {
    field: 'id',
    headerName: '',
    width: 60,
    cellStyle: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-start'
    },
    cellRenderer: (
      params: ICellRendererParams<CandidateProfileTalentListTableColumns>
    ) => {
      if (!params.data) return null;
      return actionsRenderer(params.data);
    }
  }
];
